import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getRequest } from '../../Fetch/request';
import './HLD.css';
import { UI_TEXT } from '../../Utils/constants';

export default function HLDDocument() {
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loc = useLocation();

  // First check query parameters (for HLD workflow like /hld/problem?problemId=123)
  const urlParams = new URLSearchParams(loc.search);
  let problemId = urlParams.get('problemId');

  // If no problemId in query params, check URL path (for standalone editor like /hld/1/editor)
  if (!problemId) {
    const pathParts = loc.pathname.split('/');
    problemId = pathParts[2]; // /hld/1/editor -> problemId = "1"
  }

  // If still no problemId, check localStorage for HLD problemId (for integrated editor)
  if (!problemId) {
    problemId = localStorage.getItem('current-hld-problemId');
  }

  // Extracted into a stable callback so both the mount effect and event listeners can call it
  const loadData = useCallback(() => {
    if (!problemId) {
      setLoading(false);
      return;
    }

    const problemKey = `hld-problem-${problemId}`;
    const attemptKey = `hld-attempt-${problemId}`;

    const problemData = localStorage.getItem(problemKey);
    const attemptData = localStorage.getItem(attemptKey);

    try {
      const parsedProblemData = problemData ? JSON.parse(problemData) : {};
      const parsedAttemptData = attemptData ? JSON.parse(attemptData) : {};

      // Extract only CORRECT non-functional requirements from conversation
      const nfrConversation = parsedAttemptData.nonFunctionalRequirementsConversation || [];
      const correctNFRs = [];
      for (let i = 0; i < nfrConversation.length - 1; i++) {
        const msg = nfrConversation[i];
        const next = nfrConversation[i + 1];
        if (
          msg.type === 'user' &&
          msg.content !== 'can you give me a hint?' &&
          !msg.isHintRequest &&
          next?.type === 'ai' &&
          next?.evaluation?.status === 'CORRECT'
        ) {
          correctNFRs.push(msg.content);
        }
      }

      // APIs come from DiagramDraw/SchemaDesigner — saved as apiEntities.
      const correctAPIs = (parsedAttemptData.apiEntities || [])
        .filter(entity => (entity.name || '').trim())
        .map(entity => {
          const sections = [];
          if (entity.children && Array.isArray(entity.children)) {
            entity.children.forEach(child => {
              if (child && child.type) {
                sections.push({ label: child.type, value: child.text || '' });
              }
            });
          }
          if (sections.length === 0 && entity.content && typeof entity.content === 'string') {
            entity.content.split('\n').filter(f => f.trim()).forEach(line => {
              const colonIdx = line.indexOf(':');
              if (colonIdx !== -1) {
                sections.push({ label: line.substring(0, colonIdx).trim(), value: line.substring(colonIdx + 1).trim() });
              } else {
                sections.push({ label: line.trim(), value: '' });
              }
            });
          }
          return { name: entity.name.trim(), sections };
        });

      // Extract deep dive Q&A where the user's answer got PASS
      const rawDeepDiveConversations = parsedAttemptData.deepDiveConversations || [];
      const passingDeepDives = [];
      rawDeepDiveConversations.forEach(({ question, conversation }) => {
        if (!Array.isArray(conversation)) return;
        for (let i = 0; i < conversation.length - 1; i++) {
          const msg = conversation[i];
          const next = conversation[i + 1];
          const verdict = next?.verdict || next?.evaluation?.verdict;
          if (msg.type === 'user' && next?.type === 'ai' && verdict === 'PASS') {
            passingDeepDives.push({ question, answer: msg.content });
            break;
          }
        }
      });

      setDocumentData({
        functionalRequirements: parsedProblemData.functionalRequirements || [],
        scale: parsedProblemData.scale || [],
        clarifications: parsedAttemptData.clarifications || [],
        correctNFRs,
        correctAPIs,
        passingDeepDives,
        diagramSnapshot: parsedAttemptData.diagramSnapshot || null,
        drawing: parsedAttemptData.steps?.hldDiagram?.drawing || parsedAttemptData.drawing || { shapes: [], connections: [] },
        functionalReqs: parsedProblemData.functionalRequirements || [],
        scaleEstimations: parsedProblemData.scale || [],
      });
    } catch (e) {
      console.error('Failed to parse HLD document data from localStorage:', e);
    }

    setLoading(false);
  }, [problemId]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time update: re-load whenever any component writes to hld-attempt-${problemId}
  useEffect(() => {
    if (!problemId) return;
    const attemptKey = `hld-attempt-${problemId}`;
    const handler = (e) => {
      if (e.detail?.key === attemptKey) loadData();
    };
    window.addEventListener('attemptDataUpdated', handler);
    return () => window.removeEventListener('attemptDataUpdated', handler);
  }, [problemId, loadData]);

  // Live-update the diagram snapshot whenever HldDraw saves a new one
  useEffect(() => {
    if (!problemId) return;
    const handler = (e) => {
      if (e.detail?.problemId === problemId && e.detail?.snapshot) {
        setDocumentData(prev => prev ? { ...prev, diagramSnapshot: e.detail.snapshot } : prev);
      }
    };
    window.addEventListener('hldDiagramSnapshotUpdated', handler);
    return () => window.removeEventListener('hldDiagramSnapshotUpdated', handler);
  }, [problemId]);

  // Keep backward-compat: also reload on the old localStorageUpdated event
  useEffect(() => {
    if (!problemId) return;
    const handler = () => loadData();
    window.addEventListener('localStorageUpdated', handler);
    return () => window.removeEventListener('localStorageUpdated', handler);
  }, [problemId, loadData]);

  if (loading) {
    return <div>Loading document...</div>;
  }

  if (!documentData) {
    return <div>No document data available</div>;
  }

  // Helper function to check if item has user content
  const hasUserContent = (item) => {
    // Handle different data structures
    if (!item) return false;
    
    // If item has userInput, check if it has content
    if (item.userInput !== undefined) {
      return item.userInput && item.userInput.trim();
    }
    
    // If item has text property (from TextListBuilder format)
    if (item.text !== undefined) {
      return item.text && item.text.trim();
    }
    
    // If item is a string, check if it has content
    if (typeof item === 'string') {
      return item.trim();
    }
    
    // Handle conversation structure with attempts
    if (item.attempts && Array.isArray(item.attempts)) {
      return item.attempts.some(attempt => 
        attempt && 
        attempt.userInput && 
        attempt.userInput.trim()
      );
    }
    
    return false;
  };

  const formatList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    
    const allUserResponses = [];
    
    // Handle conversation structure (from StructuredInterview)
    list.forEach(item => {
      if (item && typeof item === 'object') {
        // If it's a question with attempts
        if (item.attempts && Array.isArray(item.attempts)) {
          item.attempts.forEach(attempt => {
            if (attempt && attempt.userInput && attempt.userInput.trim()) {
              allUserResponses.push(attempt.userInput.trim());
            }
          });
        }
        // If it's a direct item with userInput
        else if (item.userInput && item.userInput.trim()) {
          allUserResponses.push(item.userInput.trim());
        }
        // If it's a text item
        else if (item.text && item.text.trim()) {
          allUserResponses.push(item.text.trim());
        }
      }
      // If it's a string
      else if (typeof item === 'string' && item.trim()) {
        allUserResponses.push(item.trim());
      }
    });
    
    return allUserResponses;
  };

  return (
    <div className="hld-document-content">
      {/* Functional Requirements & Scale */}
      <div className="hld-document-section">
        <h3>1. Functional Requirements & Scale</h3>
        <div className="hld-document-subsection" style={{ marginTop: '8px' }}>
          <h4>Functional Requirements</h4>
          {formatList(documentData.functionalRequirements || documentData.functionalReqs).length > 0 ? (
            <ol className="hld-document-list">
              {formatList(documentData.functionalRequirements || documentData.functionalReqs).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ol>
          ) : (
            <p className="hld-document-empty">{UI_TEXT.EMPTY_MESSAGES.FUNCTIONAL_REQS}</p>
          )}
        </div>
        <div className="hld-document-subsection">
          <h4>Scale & Estimations</h4>
          {formatList(documentData.scale || documentData.scaleEstimations).length > 0 ? (
            <ol className="hld-document-list">
              {formatList(documentData.scale || documentData.scaleEstimations).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ol>
          ) : (
            <p className="hld-document-empty">{UI_TEXT.EMPTY_MESSAGES.SCALE_ESTIMATIONS}</p>
          )}
        </div>
      </div>

      {/* Non-Functional Requirements */}
      <div className="hld-document-section">
        <h3>2. Non-Functional Requirements</h3>
        {(documentData.correctNFRs || []).length > 0 ? (
          <ol className="hld-document-list">
            {documentData.correctNFRs.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ol>
        ) : (
          <p className="hld-document-empty">No correct non-functional requirements defined yet</p>
        )}
      </div>

      {/* APIs */}
      <div className="hld-document-section">
        <h3>3. APIs</h3>
        {(documentData.correctAPIs || []).length > 0 ? (
          <ol className="hld-document-list">
            {documentData.correctAPIs.map((api, idx) => (
              <li key={idx} style={{ marginBottom: '12px' }}>
                <strong style={{ fontSize: '14px' }}>{api.name}</strong>
                {api.sections && api.sections.length > 0 && (
                  <div style={{ marginTop: '6px', marginLeft: '12px' }}>
                    {api.sections.map((section, sIdx) => (
                      <div key={sIdx} style={{ marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ fontWeight: '600', textTransform: 'capitalize', color: '#444' }}>{section.label}:</span>
                        {section.value ? (
                          <span style={{ color: '#555', marginLeft: '6px' }}>{section.value}</span>
                        ) : (
                          <span style={{ color: '#aaa', marginLeft: '6px', fontStyle: 'italic' }}>—</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="hld-document-empty">No APIs defined yet</p>
        )}
      </div>

      {/* HLD Diagram */}
      <div className="hld-document-section">
        <h3>4. HLD Diagram</h3>
        {documentData.diagramSnapshot ? (
          <img
            src={documentData.diagramSnapshot}
            alt="HLD Diagram"
            style={{ maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: '4px', marginTop: '8px' }}
          />
        ) : (
          <p className="hld-document-empty">No diagram created yet</p>
        )}
      </div>

      {/* Deep Dives */}
      <div className="hld-document-section">
        <h3>5. Deep Dives</h3>
        {(documentData.passingDeepDives || []).length > 0 ? (
          <ol className="hld-document-list">
            {documentData.passingDeepDives.map((item, idx) => (
              <li key={idx}>
                <strong>{item.question}</strong>
                <p style={{ margin: '4px 0 0 0', color: '#444' }}>{item.answer}</p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="hld-document-empty">No passing deep dive answers yet</p>
        )}
      </div>
    </div>
  );
}
